import "./App.css";
import * as React from "react";
import { useEffect, useState } from "react";
import {
  HierarchicalTree,
  DataBinding,
  DiagramComponent,
  SnapConstraints,
  Inject,
  DiagramTools,
} from "@syncfusion/ej2-react-diagrams";
import { useTranslation } from "react-i18next";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import {
  ItemDirective,
  ItemsDirective,
  ToolbarComponent,
} from "@syncfusion/ej2-react-navigations";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
  ConnectorModel,
  Node,
  Diagram,
  PrintAndExport,
  NodeModel,
  BpmnDiagrams,
  UndoRedo,
  NodeConstraints,
  UserHandleModel,
  SymbolPalette,
  SymbolPaletteComponent,
} from "@syncfusion/ej2-react-diagrams";
import { SwitchComponent } from "@syncfusion/ej2-react-buttons";
Diagram.Inject(UndoRedo, BpmnDiagrams);
SymbolPalette.Inject(BpmnDiagrams);
Diagram.Inject(PrintAndExport);

const createNode = (id, height, width, offsetX, offsetY, content) => ({
  id,
  height,
  width,
  offsetX,
  offsetY,
  annotations: [{ content: content }],
  shape: { type: "Path", data: "M 10 0 L 166 0 L 156 44 L 0 44 z" },
});
// Creates an ellipse node with specified properties
const createEllipseNode = (
  id,
  height,
  width,
  offsetX,
  offsetY,
  content = "",
  fill = "white"
) => ({
  id,
  width,
  height,
  offsetX,
  offsetY,
  annotations: [{ content: content }],
  shape: { type: "Basic", shape: "Ellipse" },
  style: { fill: fill },
});

// Creates a text node with specified properties
const createTextNode = (id, height, width, offsetX, offsetY, content) => ({
  id,
  width,
  height,
  offsetX,
  offsetY,
  style: { fill: "transparent", strokeWidth: 0 },
  shape: { type: "Text", content: content },
});
const createConnector = (id, source, target) => ({
  id: id,
  sourceID: source,
  targetID: target,
  style: {
    strokeDashArray: "",
  },
});

function calculateLayout(fishboneData, userConfig = {}) {
  // 1. 从 userConfig 取可覆盖的配置
  let {
    mainLineStartX = 200, // 主干起点 X
    centerY = 350, // 主干 Y
    branchSpacing = 200, // 分类间 X 间距
    boneLength = 60, // 子骨长度
    idPrefix = "fb",
    branchOffsetY, // 用户若传，直接用；否则后面动态算
    minBranchOffsetY = 160, // 鱼骨高度最小偏移
  } = userConfig;

  // 2. 先算出最大子因数量
  const maxBones = fishboneData.categories.reduce((mx, c) => {
    const n = Array.isArray(c.items) ? c.items.length : 0;
    return Math.max(mx, n);
  }, 0);

  // 3. 如果用户没传 branchOffsetY，就动态算一个
  const margin = 50; // 额外留白
  if (branchOffsetY == null) {
    branchOffsetY = boneLength * maxBones + margin;
  }
  // 4. 强制不小于 minBranchOffsetY
  branchOffsetY = Math.max(branchOffsetY, minBranchOffsetY);

  // 5. id 计数器
  let connCount = 0;
  const nextConnId = () => `${idPrefix}_conn_${connCount++}`;

  const nodes = [];
  const connectors = [];
  const mainAnchors = [];

  // 6. 鱼头
  const headId = `${idPrefix}_head`;
  nodes.push(
    createEllipseNode(
      headId,
      50,
      100,
      mainLineStartX - 100,
      centerY,
      fishboneData.start
    )
  );

  // 7. 结果节点
  const resultId = `${idPrefix}_result`;
  const half = Math.ceil(fishboneData.categories.length / 2);
  const resultX = mainLineStartX + branchSpacing * half + 50;
  nodes.push(
    createNode(resultId, 60, 180, resultX, centerY, fishboneData.result)
  );

  // 8. 各分类分支
  fishboneData.categories.forEach((cat, idx) => {
    const isTop = idx < half;
    const rowIndex = isTop ? idx : idx - half;

    const catX = mainLineStartX + rowIndex * branchSpacing;
    const catY = isTop ? centerY - branchOffsetY : centerY + branchOffsetY;

    // 主干锚点
    const anchorId = `${idPrefix}_anchor_${rowIndex}`;
    if (!mainAnchors.includes(anchorId)) {
      nodes.push(createEllipseNode(anchorId, 30, 30, catX + 50, centerY, ""));
      mainAnchors.push(anchorId);
    }

    // 分类节点
    const catId = `${idPrefix}_cat_${idx}`;
    nodes.push(createNode(catId, 40, 100, catX, catY, cat.name));

    // 主分支上先收集锚点：主干→各骨锚→分类
    const stemNodes = [anchorId];

    // 子因列表
    const bones = Array.isArray(cat.items) ? cat.items : [];
    // 主分支向量
    const dx = catX - (catX + 50);
    const dy = catY - centerY;
    const branchLen = Math.hypot(dx, dy);

    // 插入骨锚点，采用 maxBones 来算 t，保前半段分布
    bones.forEach((bone, bIdx) => {
      //const t = (bIdx + 1) / (maxBones + 1);
      const t = (maxBones - bones.length + (bIdx + 1)) / (maxBones + 1);
      const baseX = catX + 50 + dx * t;
      const baseY = centerY + dy * t;
      const baseId = `${idPrefix}_cat${idx}_base${bIdx}`;
      nodes.push(createEllipseNode(baseId, 20, 20, baseX, baseY, ""));
      stemNodes.push(baseId);
    });

    stemNodes.push(catId);

    // 主分支连线
    stemNodes.forEach((nid, i) => {
      if (i === 0) return;
      connectors.push(createConnector(nextConnId(), nid, stemNodes[i - 1]));
    });

    // 挂子骨文本
    bones.forEach((bone, bIdx) => {
      //const t = (bIdx + 1) / (maxBones + 1);
      const t = (maxBones - bones.length + (bIdx + 1)) / (maxBones + 1);
      const ux = dx / branchLen;
      const uy = dy / branchLen;
      const px = -uy;
      const py = ux;
      const sign = isTop ? 1 : -1;

      const baseId = `${idPrefix}_cat${idx}_base${bIdx}`;
      const baseNode = nodes.find((n) => n.id === baseId);

      // 文本放在骨锚“左侧”
      const endX = baseNode.offsetX - px * boneLength * sign - 20;
      const endY = baseNode.offsetY - py * boneLength * sign;

      const boneTextId = `${idPrefix}_bone_${idx}_${bIdx}`;
      nodes.push(
        createTextNode(boneTextId, 30, 80, endX, centerY + dy * t, bone)
      );
      connectors.push(createConnector(nextConnId(), boneTextId, baseId));
    });
  });

  // 9. 鱼背主干：鱼头→主锚→…→结果
  mainAnchors
    .sort((a, b) => {
      const ax = nodes.find((n) => n.id === a).offsetX;
      const bx = nodes.find((n) => n.id === b).offsetX;
      return ax - bx;
    })
    .forEach((aid, i, arr) => {
      if (i === 0) {
        connectors.push(createConnector(nextConnId(), headId, aid));
      } else {
        connectors.push(createConnector(nextConnId(), arr[i - 1], aid));
      }
      if (i === arr.length - 1) {
        connectors.push(createConnector(nextConnId(), aid, resultId));
      }
    });

  return { nodes, connectors };
}
// CSS styles for custom elements in the diagram
const sample_css = `

.diagram-workflow #toolbarContainer {
  display: flex;
  align-items: center;
  height: 62px !important;
  width: 100% !important;
  position: relative;  
  justify-content: center;
}
.diagram-workflow #toolbar {
  width: 80% !important;
  height: 62px !important;
  flex-grow: 1;
}
.diagram-workflow #toolbar_diagram {
  height: 62px !important;
}
.diagram-workflow #toggleSwitchContainer {
  width: 20% !important;
  height: 62px !important;
  display: flex;
  justify-content: center;
  margin-left: auto;
  background: #f9fafb
}

.diagram-workflow #toolbar .e-toolbar-item{
  padding: 0px !important;
}
.diagram-workflow #symbolPalette {
  overflow: hidden !important;
  height: 100%; 
  display: flex;
  align-items: center;
  margin-top: 15px;
}
.diagram-workflow #symbolPaletteComponent_container{
  border: none !important;
}
.diagram-workflow #symbolPalette_container{
  border: none !important;
}
.diagram-workflow #symbolPalette .e-acrdn-header{
  display: none;
}
.diagram-workflow #symbolPalette .e-acrdn-content{
  padding: 0;
  background-color: transparent !important;
}
.diagram-workflow #symbolPalette .e-remove-palette{
  background-color: transparent !important;
}
.diagram-workflow #symbolPalette .e-accordion{
  background-color: transparent !important;
}
.diagram-workflow #symbolPalette .e-accordion .e-acrdn-item.e-selected.e-select.e-active{
  background-color: transparent !important;
}
.diagram-workflow #diagramContainer {
    flex: 1;
}
.diagram-workflow #diagram {
    width: 100%;
    height: 100%;
    border: 1px solid transparent;
}
.diagram-workflow #switch-container{
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    right: 0; 
    margin-right: 10px;
    margin-top: 15px;
}
/* To display the loading sign */
  .diagram-workflow .diagram-loading-indicator {
    border: 4px solid lightgrey;
    border-top: 4px solid grey;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 2s linear infinite;
  }

/* To display the success sign */
.diagram-workflow .diagram-tick {
  display: none;
  animation: showTick 0.5s forwards;
  width: 14px;
  height: 14px;
  padding: 1px;
  line-height: 1;
  color: white;
  border-radius: 50%;
  background-color:green;
}
.diagram-workflow .e-play {
  content: '\e70C';
}
/* To display the failure sign */
.diagram-workflow .cross {
display: none;
animation: showTick 0.5s 0.2s forwards;
font-size: 10px;
width:15px;height:15px;
padding-left: 4.5px;
padding-top: 2.5px;
line-height: 1;
color: white;
border-radius: 50%;
background-color:red;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes showTick {
  0% { opacity: 0; transform: scale(0.5); }
  100% { opacity: 1; transform: scale(1); }
}
  `;
// Declare variables for diagram instance, toolbar instance, and workflow management
let diagramInstance;
let toolbarEditor;
let switchTooltipRef;
let switchRef;
let switchContainerRef;
let symbolPaletteRef;
let isEditMode = false;
// React component for the BPMN workflow editor
function Fishbone() {
  const { t } = useTranslation();
  const query = new URLSearchParams(window.location.search);
  const showNav = query.get("ae") === "1"; // ae=1 时显示导航
  const fishboneId = query.get("id") || null; // id 参数
  const tenantId = query.get("tenantId");
  const [nodes, setNodes] = React.useState([]);
  const [connectors, setConnectors] = React.useState([]);
  const [loading, setLoading] = useState(!!fishboneId);
  React.useEffect(() => {
    // 使用defaultFishboneData生成图表
    applyModeState(true);
  }, []);
  // symbol palette template
  const paletteTemplate = () => (
    <aside
      id="symbolPalette"
      style={{
        display: isEditMode || true ? "flex" : "none",
        height: "100%",
        alignItems: "center",
        border: "none",
      }}
    >
      <SymbolPaletteComponent
        id="symbolPaletteComponent"
        ref={(symbolPaletteComponent) =>
          (symbolPaletteRef = symbolPaletteComponent)
        }
        width="100%"
        height="62px"
        symbolHeight={45}
        symbolWidth={45}
        enableAnimation={false}
        showHeader={true}
        palettes={[
          {
            id: "BPMN",
            expanded: true,
            symbols: [
              {
                id: "Start",
                shape: {
                  type: "Path",
                  data: "M 10 0 L 166 0 L 156 44 L 0 44 z",
                },
                annotations: [{ content: "" }],
                tooltip: { content: "", relativeMode: "Object" },
                constraints: NodeConstraints.Default | NodeConstraints.Tooltip,
              },
              {
                id: "Description",
                shape: { type: "Basic", shape: "Ellipse" },
                annotations: [{ content: "" }],
                tooltip: { content: "", relativeMode: "Object" },
                constraints: NodeConstraints.Default | NodeConstraints.Tooltip,
              },
              {
                id: "TextNode",
                shape: {
                  type: "Text",
                  content: "Text",
                },
                style: { fontSize: 16 },
                constraints: NodeConstraints.Default | NodeConstraints.Tooltip,
              },
            ],
            iconCss: "",
          },
        ]}
        getSymbolInfo={() => ({ fit: true })}
        paletteExpanding={(args) => {
          args.cancel = true;
        }}
      />
    </aside>
  );
  // function to handle toolbar items click
  function tooledit(args) {
    switch (args.item && args.item.text) {
      case t("New"):
        diagramInstance.clear();
        break;
      case t("Open"):
        document.getElementById("fileInput").click();
        break;
      case t("Save"):
        saveImage();
        break;
      case t("Delete"):
        diagramInstance.remove();
        break;
      case t("Select"):
        diagramInstance.tool = DiagramTools.Select;
        break;
      case t("Pan"):
        diagramInstance.tool = DiagramTools.ZoomPan;
        break;
    }
  }
  // start Workflow animation

  var input = document.createElement("input");
  input.type = "file";
  input.accept = ".json";
  input.id = "fileInput";
  input.style.display = "none";
  document.body.appendChild(input);
  input.addEventListener("change", function (e) {
    var file = e.target.files[0];
    if (!file) return;
    var reader = new FileReader();
    reader.onload = function (evt) {
      var json = JSON.parse(evt.target.result);
      diagramInstance.loadDiagram(json);
      diagramInstance.fitToPage();
      diagramInstance.tool = DiagramTools.ZoomPan;
      input.value = "";
    };
    reader.readAsText(file);
  });

  function saveImage() {
    var options = {};
    options.mode = "Data";
    options.format = "PNG";
    var data = diagramInstance.exportDiagram(options);

    var jsonData = diagramInstance.saveDiagram();

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: fishboneId,
        tenantId: tenantId,
        imageBase64: data,
        jsonString: jsonData,
      }),
    };
    fetch("/api/v1/fishbone", requestOptions).then((response) => {
        toast.success(t("SaveSuccess"), { hideProgressBar: true });
    });
  }

  useEffect(() => {
    async function fetchData() {
      try {
        if (!fishboneId) return;
        // 开始 loading
        setLoading(true);
        const resp = await fetch(
          `/api/v1/fishbone?id=${fishboneId}&tenantId=${tenantId}`
        );
        const data = await resp.json();
        if (data.result.jsonString) {
          var json = JSON.parse(data.result.jsonString);
            diagramInstance.loadDiagram(json);
            diagramInstance.tool = DiagramTools.SingleSelect;
        } else {
          const { nodes, connectors } = calculateLayout(data.result);
          setNodes(nodes);
          setConnectors(connectors);
        }
      } catch (e) {
        console.error(e);
        // 出错时可以 fallback 到默认
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [fishboneId, tenantId]);

  let userHandles = [
    {
      name: "delete",
      pathData:
        "M0.97,3.04 L12.78,3.04 L12.78,12.21 C12.78,12.64,12.59,13,12.2,13.3 C11.82,13.6,11.35,13.75,10.8,13.75 L2.95,13.75 C2.4,13.75,1.93,13.6,1.55,13.3 C1.16,13,0.97,12.64,0.97,12.21 Z M4.43,0 L9.32,0 L10.34,0.75 L13.75,0.75 L13.75,2.29 L0,2.29 L0,0.75 L3.41,0.75 Z",
      tooltip: { content: t("DeleteNode") },
      side: "Bottom",
      offset: 0.5,
      margin: { bottom: 5 },
      disableConnectors: true,
    },
    {
      name: "drawConnector",
      pathData:
        "M6.09,0 L13.75,6.88 L6.09,13.75 L6.09,9.64 L0,9.64 L0,4.11 L6.09,4.11 Z",
      tooltip: { content: t("DrawConnector") },
      side: "Right",
      offset: 0.5,
      margin: { right: 5 },
      disableConnectors: true,
    },
  ];
  // functions on toggle switch change
  function switchChange(args) {
    isEditMode = args;
    applyModeState(args);
    updateTooltipContent(args);
  }
  // function to update tooltip content based on switch state
  function updateTooltipContent(isChecked) {
    if (switchTooltipRef) {
      if (isChecked) {
        switchTooltipRef.content = "Disable Editing";
      } else {
        switchTooltipRef.content = "Enable Editing";
      }
    }
  }
  function renderComplete() {
    diagramInstance.fitToPage({ mode: "Width" });
  }

  const [snapSettings, setSnapSettings] = React.useState({
    constraints: isEditMode ? SnapConstraints.ShowLine : SnapConstraints.None,
  });
  // function to enable or disable tool bar buttons based on editing mode
  function applyModeState(isEditMode) {
    var buttonsToToggle = [t("Select"), t("Delete"), t("Save")];
    if (toolbarEditor) {
      toolbarEditor.items.forEach(function (item) {
        if (buttonsToToggle.includes(item.id)) {
          item.disabled = !isEditMode;
        }
      });
      // Hide last separator in toolbar if palette is hidden
      var items = toolbarEditor.items;
      var lastSepIndex = items.findIndex(function (item) {
        return item.type === "Separator" && items.indexOf(item) > 7;
      });
      if (lastSepIndex !== -1) {
        items[lastSepIndex].visible = isEditMode;
      }
      toolbarEditor.refresh();
      const paletteEl = document.getElementById("symbolPalette");
      if (paletteEl) {
        paletteEl.style.display = isEditMode ? "flex" : "none";
      }
      diagramInstance.tool = isEditMode
        ? DiagramTools.SingleSelect
        : DiagramTools.ZoomPan;
    }
  }
  // update the label color based on toolbar button color
  var toolbarButton = document.querySelector(".e-toolbar-item button");
  if (toolbarButton) {
    var buttonStyle = window.getComputedStyle(toolbarButton);
    var buttonColor = buttonStyle.color;
    var editLabel = document.getElementById("editLabel");
    editLabel.style.color = buttonColor;
  }

  return (
    <div
      className="control-pane diagram-workflow"
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{sample_css}</style>
          <ToastContainer />
      <div id="toolbarContainer" style={{ height: 62, display: "flex" }}>
        <div id="toolbar">
          <ToolbarComponent
            ref={(toolbar) => (toolbarEditor = toolbar)}
            id="toolbar_diagram"
            clicked={tooledit}
            overflowMode="Popup"
          >
            <ItemsDirective>
              <ItemDirective
                id="Save"
                tooltipText={t("SaveDiagram")}
                text={t("Save")}
                prefixIcon="e-icons e-save"
              />
              <ItemDirective type="Separator" />
              <ItemDirective
                id="Delete"
                tooltipText={t("DeleteSelected")}
                text={t("Delete")}
                prefixIcon="e-icons e-trash"
              />
              <ItemDirective type="Separator" />
              <ItemDirective
                id="Select"
                tooltipText={t("SelectTool")}
                text={t("Select")}
                prefixIcon="e-icons e-mouse-pointer"
                overflow="Show"
              />
              <ItemDirective
                id="Pan"
                tooltipText={t("PanTool")}
                text={t("Pan")}
                prefixIcon="e-icons e-pan"
                overflow="Show"
              />
              <ItemDirective type="Separator" />
              <ItemDirective
                id="palette"
                template={paletteTemplate}
                overflow="Show"
              />
            </ItemsDirective>
          </ToolbarComponent>
        </div>
        <div id="toggleSwitchContainer" className="d-none">
          <div
            id="switch-container"
            ref={(switchContainer) => (switchContainerRef = switchContainer)}
          >
            <span
              id="editLabel"
              style={{ fontSize: "14px", marginRight: "6px" }}
            >
              Edit
            </span>
            <TooltipComponent
              id="switch_tooltip_digram"
              ref={(switchTooltip) => (switchTooltipRef = switchTooltip)}
              content="Enable Editing"
              position="TopCenter"
            >
              <SwitchComponent
                id="DiagramToggleSwitch"
                ref={(DiagramToggleSwitch) => (switchRef = DiagramToggleSwitch)}
                checked={isEditMode}
                change={(args) => switchChange(args.checked)}
              />
            </TooltipComponent>
          </div>
        </div>
      </div>
      <div
        style={{
          padding: "8px 16px",
          backgroundColor: "#f0f8ff",
          border: "1px solid #b0e0e6",
          borderRadius: "4px",
          marginBottom: "8px",
          fontSize: "14px",
          color: "#333",
        }}
      >
        {t("SaveReminder")}
      </div>
      <div style={{ flex: 1, position: "relative" }}>
        {loading && (
          <div
            className="diagram-loading-indicator"
            style={{
              position: "absolute",
              placeSelf: "anchor-center",
              top: 10,
              right: 10,
              zIndex: 10,
            }}
          />
        )}
        <DiagramComponent
          id="diagram"
          ref={(diagram) => (diagramInstance = diagram)}
          width={"100%"}
          height="100%"
          snapSettings={snapSettings}
          nodes={nodes}
          connectors={connectors}
          selectedItems={{ userHandles: userHandles }}
          onUserHandleMouseDown={(args) => {
            var handleName = args.element.name;
            switch (handleName) {
              case "delete":
                diagramInstance.remove(diagramInstance.selectedItems.nodes[0]);
                break;
              case "drawConnector":
                var sourceNode = diagramInstance.selectedItems.nodes[0];
                if (!sourceNode) return;
                diagramInstance.drawingObject = {
                  type: "Straight",
                  sourceID: sourceNode.id,
                };
                diagramInstance.tool = DiagramTools.DrawOnce;
                break;
            }
          }}
          // Sets default properties for connectors.
          getConnectorDefaults={(connector) => {
            connector.targetDecorator = {
              shape: "Arrow",
              width: 5,
              height: 5,
            };
            // Customizes target decorator style for specific connectors.
          }}
          tool={DiagramTools.ZoomPan}
        >
          <Inject services={[DataBinding, HierarchicalTree]} />
        </DiagramComponent>
      </div>
    </div>
  );
}
export default Fishbone;
